import { Button, ButtonGroup, Tab, Tabs, Typography } from '@material-ui/core';
import { fade, makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import React, { useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ActiveHubsLevelSamples,
  GrainBinLevelSample,
  GrainBinTicketLevelSample,
  withGetGrainContainerHistoryGrainLevelHoc,
  WithGetGrainContainerHistoryGrainLevelHocChildProps,
  withGetGrainContainerHistoryGrainTicketsLevelHoc,
  WithGetGrainContainerHistoryGrainTicketsLevelHocChildProps,
  withGetSensorGeneratedGrainTicketsHoc,
  WithGetSensorGeneratedGrainTicketsHocChildProps,
} from '../../../../api';

import { ActiveStoragePeriodContext } from '../../../../contexts/activeStoragePeriod';
import { amber_amber, white } from '../../../../style';
import { ContainerTypeLegacy, RelativePeriods } from '../../../../util/constant';
import { CenteredSpinner } from '../../../spinner';
import { GRAIN_TICKET_APPROVE_STATE } from '../../manage-grain-tickets/manage-pending-grain-tickets';
import { SummaryCard } from '../SummaryCard';
import DualLevelHistoryPlot from './DualLevelHistoryPlot';
import { LevelHistoryCardHeaderAction } from './LevelHistoryCardHeaderAction';
import { isValidDate } from './LevelHistoryPlotHelpers';
import LevelHistoryPlotV2 from './LevelHistoryPlotV2';
import TapeDropHistoryPlot from './TapeDropHistoryPlot';

const useStyles = makeStyles((theme) => ({
  root_0: {
    paddingBottom: 25,
  },
  root_1: {
    paddingBottom: 0,
  },
  spinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 196,
    height: '100%',
  },
  selectedButton: {
    backgroundColor: amber_amber,
    color: white,
    '&:hover': {
      backgroundColor: fade(amber_amber, 0.8),
    },
  },
  tabs: {
    minHeight: 36,
    height: 36,
    marginBottom: 10,
  },
  tab: {
    minHeight: 36,
    height: 36,
    minWidth: 0,
    fontSize: 11,
    fontWeight: 500,
    fontFamily: '"Source Sans Pro", sans-serif',
    [theme.breakpoints.up('sm')]: {
      fontSize: 15,
    },
  },
  tabLabelContainer: {
    padding: 0,
  },
}));

export type LevelHistoryCardPropsType = {
  container_id: number;
  container_type: ContainerTypeLegacy;
  as_of: Date;
  period: string;
  onSelectPeriod?: (value: RelativePeriods) => any;
  height?: number;
  width?: number;
  grain_bin_location_timezone: string;
  url_base: string;
  minHeight?: number;
  maxHeight?: number;
  title: string;
  showWithoutSummaryCard?: boolean;
  show_grain_ticket_level_deviation_warning: boolean;
  hideManageGrainTicketsBtn?: boolean;
} & WithGetGrainContainerHistoryGrainLevelHocChildProps &
  WithGetSensorGeneratedGrainTicketsHocChildProps &
  WithGetGrainContainerHistoryGrainTicketsLevelHocChildProps;

export const LevelHistoryCard = withGetSensorGeneratedGrainTicketsHoc(
  withGetGrainContainerHistoryGrainLevelHoc(
    withGetGrainContainerHistoryGrainTicketsLevelHoc(
      ({
        container_id,
        container_type,
        url_base,
        as_of,
        period,
        title,
        onSelectPeriod,
        loading,
        height,
        width,
        minHeight,
        maxHeight,
        grain_bin_location_timezone,
        grain_bin_level_history,
        grain_bin_tickets_level_history,
        is_bin_tickets_history_loading,
        showWithoutSummaryCard,
        show_grain_ticket_level_deviation_warning,
        hideManageGrainTicketsBtn = false,
        get_sensor_generated_grain_tickets_loading,
        sensor_generated_grain_tickets,
      }: LevelHistoryCardPropsType) => {
        console.log('Grain Bin Level History:', grain_bin_level_history);
        console.log('Grain Bin Tickets History:', grain_bin_tickets_level_history);

        console.log('sensor_generated_grain_tickets', sensor_generated_grain_tickets);
        const [tab, setTab] = useState(0);

        const classes = useStyles();
        const dispatch = useDispatch();
        const ctx = useContext(ActiveStoragePeriodContext);
        const activeStoragePeriod = ctx.activeStoragePeriod;
        const today = DateTime.local();
        today.set({ hour: 12 });
        const { max_bushels, active_hubs_level_samples } = grain_bin_level_history;
        const { max_height_ft } = grain_bin_level_history;
        const { grain_tickets_level_samples } = grain_bin_tickets_level_history;
        const {
          no_grain_tickets_created,
          grain_tickets_bushel_level_on_period_start,
          grain_tickets_bushel_level_on_period_end,
        } = grain_bin_tickets_level_history;

        type FormattedHubLevelData = {
          hub_id: number;
          bushelsData: {
            headspace_ft: number;
            hub_ft: number;
            timestamp: number;
            quantity: number;
            x: number;
            y: number;
            sidewall_tape_drop_ft: number | null;
            center_tape_drop_ft: number | null;
            eave_tape_drop_ft: number | null;
          }[];
          has_pair_level_sensor: boolean;
          has_level_sensor: boolean;
          has_aux_level_sensor: boolean;
        };

        const active_hubs_formatted_level_data: FormattedHubLevelData[] =
          active_hubs_level_samples.length > 0
            ? active_hubs_level_samples.map(
                ({
                  hub_id,
                  level_samples,
                  has_pair_level_sensor,
                  has_level_sensor,
                  has_aux_level_sensor,
                }: ActiveHubsLevelSamples) => {
                  const bushelsData =
                    level_samples.length > 0
                      ? level_samples
                          .filter(({ epoch_time: dt }: GrainBinLevelSample) => {
                            if (
                              activeStoragePeriod &&
                              activeStoragePeriod.start_date.getTime() <= new Date(dt).getTime() &&
                              new Date(dt).getTime() <=
                                (activeStoragePeriod.end_date
                                  ? activeStoragePeriod.end_date.getTime()
                                  : Infinity)
                            ) {
                              return isValidDate(dt, period, today);
                            }
                            if (activeStoragePeriod === null) {
                              return isValidDate(dt, period, today);
                            }
                            return false;
                          })
                          .map(
                            ({
                              epoch_time,
                              bushel,
                              headspace_ft,
                              hub_ft,
                              total_grain_volume_bushels: quantity,
                              sidewall_tape_drop_ft,
                              center_tape_drop_ft,
                              eave_tape_drop_ft,
                            }: GrainBinLevelSample) => ({
                              headspace_ft,
                              hub_ft,
                              sidewall_tape_drop_ft:
                                sidewall_tape_drop_ft != null ? sidewall_tape_drop_ft : null,
                              center_tape_drop_ft:
                                center_tape_drop_ft != null ? center_tape_drop_ft : null,
                              eave_tape_drop_ft:
                                eave_tape_drop_ft != null ? eave_tape_drop_ft : null,
                              timestamp: DateTime.fromMillis(new Date(epoch_time).getTime())
                                .set({ second: 0 })
                                .toMillis() as number,
                              // If has_pair_level_sensor is true, use total_grain_volume_bushels,
                              // otherwise use bushel given we know it's a single pair.
                              quantity: has_pair_level_sensor ? quantity : bushel,
                              x: DateTime.fromMillis(new Date(epoch_time).getTime())
                                .set({ second: 0 })
                                .toMillis() as number,
                              y: (has_pair_level_sensor ? quantity : bushel) as number,
                            })
                          )
                          .sort((a, b) => a.timestamp - b.timestamp)
                      : [];
                  return {
                    hub_id,
                    bushelsData,
                    has_pair_level_sensor,
                    has_level_sensor,
                    has_aux_level_sensor,
                  };
                }
              )
            : [];

        // Grain Tickets within Active Storage Period
        let active_grain_tickets_level_data =
          grain_tickets_level_samples.length > 0
            ? grain_tickets_level_samples
                .filter(({ epoch_time: dt }: GrainBinTicketLevelSample) => {
                  if (
                    activeStoragePeriod &&
                    activeStoragePeriod.start_date.getTime() <= new Date(dt).getTime() &&
                    new Date(dt).getTime() <=
                      (activeStoragePeriod.end_date
                        ? activeStoragePeriod.end_date.getTime()
                        : Infinity)
                  ) {
                    return true;
                  }
                  return activeStoragePeriod === null;
                })
                .map(({ epoch_time, bushel }: GrainBinTicketLevelSample) => ({
                  timestamp: DateTime.fromMillis(new Date(epoch_time).getTime()).toMillis(),
                  quantity: bushel,
                  x: DateTime.fromMillis(new Date(epoch_time).getTime())
                    .set({ second: 0 })
                    .toMillis(),
                  y: bushel,
                }))
                .sort((a, b) => a.timestamp - b.timestamp)
            : [];

        // added grain bushel level on period start and end
        if (
          grain_tickets_bushel_level_on_period_start &&
          grain_tickets_bushel_level_on_period_end
        ) {
          active_grain_tickets_level_data = [
            {
              x: DateTime.fromMillis(
                new Date(grain_tickets_bushel_level_on_period_start.epoch_time).getTime()
              ).toMillis(),
              y: grain_tickets_bushel_level_on_period_start.bushel,
              timestamp: DateTime.fromMillis(
                new Date(grain_tickets_bushel_level_on_period_start.epoch_time).getTime()
              ).toMillis(),
              quantity: grain_tickets_bushel_level_on_period_start.bushel,
            },
            ...active_grain_tickets_level_data,
            {
              x: DateTime.fromMillis(
                new Date(grain_tickets_bushel_level_on_period_end.epoch_time).getTime()
              ).toMillis(),
              y: grain_tickets_bushel_level_on_period_end.bushel,
              timestamp: DateTime.fromMillis(
                new Date(grain_tickets_bushel_level_on_period_end.epoch_time).getTime()
              ).toMillis(),
              quantity: grain_tickets_bushel_level_on_period_end.bushel,
            },
          ];
        }

        console.log('active_hubs_formatted_level_data', active_hubs_formatted_level_data);
        console.log('active_grain_tickets_level_data', active_grain_tickets_level_data);

        const applyHeaderStyles =
          show_grain_ticket_level_deviation_warning && !hideManageGrainTicketsBtn;

        // last Pending Grain Ticket Generated Days Text
        const sortedGrainBinTickets = useMemo(() => {
          const safe_arr = sensor_generated_grain_tickets ? sensor_generated_grain_tickets : [];
          return safe_arr.sort(
            (a, b) => new Date(b.epoch_time).getTime() - new Date(a.epoch_time).getTime()
          );
        }, [JSON.stringify(sensor_generated_grain_tickets)]);
        const pendingGrainTickets = useMemo(() => {
          return sortedGrainBinTickets.filter(
            ({ approve_state }) => approve_state === GRAIN_TICKET_APPROVE_STATE.pending
          );
        }, [JSON.stringify(sortedGrainBinTickets)]);
        const lastPendingGrainTicket =
          pendingGrainTickets.length > 0 ? pendingGrainTickets[0] : null;
        const lastPendingGrainTicketGeneratedDaysText: string = useMemo(() => {
          if (
            grain_bin_location_timezone &&
            show_grain_ticket_level_deviation_warning &&
            lastPendingGrainTicket
          ) {
            const lastPendingGrainTicketGeneratedOn: Date = new Date(
              lastPendingGrainTicket.epoch_time
            );
            if (lastPendingGrainTicketGeneratedOn) {
              const asOf = DateTime.local().setZone(grain_bin_location_timezone);
              const lastPendingGrainTicketGeneratedOnDt = DateTime.fromMillis(
                new Date(lastPendingGrainTicketGeneratedOn).getTime()
              ).setZone(grain_bin_location_timezone);
              const daysDiff = Math.round(
                asOf.diff(lastPendingGrainTicketGeneratedOnDt, ['days']).toObject().days
              );
              return daysDiff === 0 ? `Today` : `${daysDiff} Days Ago`;
            }
          }
          return '';
        }, [
          grain_bin_location_timezone,
          show_grain_ticket_level_deviation_warning,
          lastPendingGrainTicket,
        ]);

        const hasEmptyPlaceHolder =
          !loading &&
          !is_bin_tickets_history_loading &&
          (grain_bin_level_history &&
            grain_bin_level_history.active_hubs_level_samples.length === 0 &&
            no_grain_tickets_created);

        const EmptyPlaceholder = () => (
          <div
            style={{
              height: '100%',
              minHeight: 100,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              No Grain Level Data
              <br /> Add a Grain Ticket to start tracking
            </Typography>
          </div>
        );

        const active_quantity_hubs_formatted_level_data = active_hubs_formatted_level_data.map(
          ({ hub_id, bushelsData }) => ({ bushelsData, hub_id })
        );

        const active_headspace_formatted_level_data = active_hubs_formatted_level_data.map(
          (hub) => ({
            hub_id: hub.hub_id,
            plotData: hub.has_level_sensor
              ? hub.bushelsData.map((d) => ({
                  x: d.timestamp,
                  y: d.headspace_ft,
                }))
              : [],
          })
        );
        const active_hub_formatted_level_data = active_hubs_formatted_level_data.map((hub) => {
          return {
            hub_id: hub.hub_id,
            plotData: hub.has_aux_level_sensor
              ? hub.bushelsData.map((d) => ({
                  x: d.timestamp,
                  y: d.hub_ft,
                }))
              : [],
          };
        });

        // Tape drop series — values computed by the backend and returned on GrainBinLevelSample
        const active_sidewall_tape_drop_data = active_hubs_formatted_level_data.map((hub) => ({
          hub_id: hub.hub_id,
          plotData: hub.has_pair_level_sensor
            ? hub.bushelsData
                .filter((d) => d.sidewall_tape_drop_ft != null)
                .map((d) => ({ x: d.timestamp, y: d.sidewall_tape_drop_ft as number }))
            : [],
        }));

        const active_center_tape_drop_data = active_hubs_formatted_level_data.map((hub) => ({
          hub_id: hub.hub_id,
          plotData: hub.has_pair_level_sensor
            ? hub.bushelsData
                .filter((d) => d.center_tape_drop_ft != null)
                .map((d) => ({ x: d.timestamp, y: d.center_tape_drop_ft as number }))
            : [],
        }));

        const active_eave_tape_drop_data = active_hubs_formatted_level_data.map((hub) => ({
          hub_id: hub.hub_id,
          plotData:
            hub.has_level_sensor && !hub.has_pair_level_sensor
              ? hub.bushelsData
                  .filter((d) => d.eave_tape_drop_ft != null)
                  .map((d) => ({ x: d.timestamp, y: d.eave_tape_drop_ft as number }))
              : [],
        }));

        // Key-Point: Define the Render Level Chart function here, so that it can be reused in SummaryCard and LevelHistoryCardHeaderAction
        const RenderLevelChart = () => (
          <div
            style={{
              minHeight: minHeight && minHeight,
              maxHeight: maxHeight && maxHeight,
              padding: '10px 0px',
            }}
          >
            <Tabs
              value={tab}
              onChange={(_e, val) => setTab(val)}
              indicatorColor="secondary"
              variant="fullWidth"
              className={classes.tabs}
            >
              <Tab
                label="Quantity"
                classes={{ root: classes.tab, fullWidth: classes.tabLabelContainer }}
                fullWidth
              />
              <Tab
                label="Grain Depth"
                classes={{ root: classes.tab, fullWidth: classes.tabLabelContainer }}
                fullWidth
              />
              <Tab
                label="Tape Drop"
                classes={{ root: classes.tab, fullWidth: classes.tabLabelContainer }}
                fullWidth
              />
            </Tabs>

            {hasEmptyPlaceHolder ? (
              <>
                <EmptyPlaceholder />
              </>
            ) : (
              <>
                <div className={`${onSelectPeriod ? classes.root_1 : classes.root_0}`}>
                  {loading ||
                  is_bin_tickets_history_loading ||
                  get_sensor_generated_grain_tickets_loading ? (
                    <div style={height ? { minHeight: height } : {}} className={classes.spinner}>
                      <CenteredSpinner fadeIn="none" />
                    </div>
                  ) : (
                    <>
                      {tab === 0 && (
                        <LevelHistoryPlotV2
                          grain_bin_location_timezone={grain_bin_location_timezone}
                          period={period}
                          active_hubs_formatted_level_data={
                            active_quantity_hubs_formatted_level_data
                          }
                          active_grain_tickets_level_data={active_grain_tickets_level_data}
                          max_bushels={max_bushels}
                        />
                      )}
                      {tab === 1 && (
                        <DualLevelHistoryPlot
                          grain_bin_location_timezone={grain_bin_location_timezone}
                          period={period}
                          active_headspace_formatted_level_data={
                            active_headspace_formatted_level_data
                          }
                          active_hub_formatted_level_data={active_hub_formatted_level_data}
                          active_grain_tickets_level_data={active_grain_tickets_level_data}
                          active_quantity_formatted_level_data={active_hubs_formatted_level_data.map(
                            (hub) => ({
                              hub_id: hub.hub_id,
                              plotData: hub.bushelsData.map((d) => ({
                                x: d.timestamp,
                                y: d.quantity,
                              })),
                            })
                          )}
                          max_bushels={max_bushels}
                          max_height_ft={max_height_ft}
                        />
                      )}
                      {tab === 2 && (
                        <TapeDropHistoryPlot
                          grain_bin_location_timezone={grain_bin_location_timezone}
                          period={period}
                          active_sidewall_tape_drop_data={active_sidewall_tape_drop_data}
                          active_center_tape_drop_data={active_center_tape_drop_data}
                          active_eave_tape_drop_data={active_eave_tape_drop_data}
                          active_grain_tickets_level_data={active_grain_tickets_level_data}
                          max_height_ft={max_height_ft}
                        />
                      )}
                    </>
                  )}
                  {onSelectPeriod && (
                    <ButtonGroup
                      color="secondary"
                      size="small"
                      style={{ marginTop: 15, display: 'flex', justifyContent: 'center' }}
                    >
                      <Button
                        className={period === RelativePeriods.day ? classes.selectedButton : ''}
                        onClick={() => onSelectPeriod(RelativePeriods.day)}
                      >
                        1D
                      </Button>
                      <Button
                        className={period === RelativePeriods.week ? classes.selectedButton : ''}
                        onClick={() => onSelectPeriod(RelativePeriods.week)}
                      >
                        1W
                      </Button>
                      <Button
                        className={period === RelativePeriods.month ? classes.selectedButton : ''}
                        onClick={() => onSelectPeriod(RelativePeriods.month)}
                      >
                        1M
                      </Button>
                      <Button
                        className={period === RelativePeriods.quarter ? classes.selectedButton : ''}
                        onClick={() => onSelectPeriod(RelativePeriods.quarter)}
                      >
                        3M
                      </Button>
                    </ButtonGroup>
                  )}
                </div>
              </>
            )}
          </div>
        );

        return showWithoutSummaryCard ? (
          <>
            <RenderLevelChart />
          </>
        ) : (
          <>
            <SummaryCard
              title={title}
              headerStyle={applyHeaderStyles ? { display: 'flex', alignItems: 'start' } : undefined}
              header_action={
                !loading &&
                !is_bin_tickets_history_loading && (
                  <LevelHistoryCardHeaderAction
                    url_base={url_base}
                    showGrainTicketLevelDeviationWarning={show_grain_ticket_level_deviation_warning}
                    hideManageGrainTicketsBtn={hideManageGrainTicketsBtn}
                    noGrainTicketsCreated={no_grain_tickets_created}
                    lastPendingGrainTicketGeneratedDaysText={
                      lastPendingGrainTicketGeneratedDaysText
                    }
                  />
                )
              }
            >
              <RenderLevelChart />
            </SummaryCard>
          </>
        );
      }
    )
  )
);
